/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import useAbortController from './useAbortController';
import axiosCall from '../../services/axios';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';
import { safeArr, safeFullName } from '../UtilityFunctions';

const useBorrowersInOrder = () => {
  const { get, reset } = useAbortController();
  const queryClient = useQueryClient();
  const { token } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const queryKey = useMemo(() => ['borrowersInOrder', loanNumber, token], [loanNumber, token]);

  const fetchBorrowersInApplicationOrder = useCallback(async () => {
    try {
      if (!loanNumber || !token)
        return {
          data: {
            dynamicBorrowers: [],
            nonBorrowingPartiesList: [],
            borrowersToRender: [],
          },
        };
      const listRes: BorrowerData[] = await axiosCall(
        { url: process.env.REACT_APP_BRWR_LIST.replace('?', loanNumber), method: 'get' },
        reset(),
        {
          'Session-Token': token,
        },
      );
      if (!Array.isArray(listRes)) throw new Error('Bad borrower list');

      const byNo: Record<string, BorrowerData> = {};
      listRes.forEach((b: BorrowerData) => {
        byNo[b.borrowerNumber] = b;
      });

      const appRes = await axiosCall(
        { url: process.env.REACT_APP_APPLICATION_LIST.replace('?', loanNumber), method: 'get' },
        reset(),
        {
          'Session-Token': token,
        },
      );
      if (!Array.isArray(appRes)) throw new Error('Bad application list');

      const orderedNumbers: string[] = [];
      appRes.forEach((app: any) => {
        const { borrowerNumber: main, coBorrowerNumber: co } = app;
        if (main && !orderedNumbers.includes(main)) orderedNumbers.push(main);
        if (typeof co === 'string' && co.trim() && !orderedNumbers.includes(co)) {
          orderedNumbers.push(co);
        }
      });

      const orderedBorrowers = safeArr(orderedNumbers.map((n) => byNo[n]));
      const leftoverBorrowers = listRes
        .filter((b) => !orderedNumbers.includes(b.borrowerNumber))
        .filter((b) => b.borrowerOnNote && b.borrowerTypeKeyword !== 'REMOVED');

      const finalBorrowerOrder = [...orderedBorrowers, ...leftoverBorrowers];

      const normalizedFinal = finalBorrowerOrder.map((b) => ({ ...b, fullName: safeFullName(b) }));

      const nbp = listRes
        .filter((b) => !b.borrowerOnNote && b.borrowerTypeKeyword !== 'REMOVED')
        .map((b) => ({ ...b, fullName: safeFullName(b) }));
      return {
        data: {
          dynamicBorrowers: normalizedFinal,
          nonBorrowingPartiesList: nbp,
          borrowersToRender: finalBorrowerOrder.map((b) => b?.borrowerNumber).concat(nbp.map((b) => b?.borrowerNumber)),
        },
      };
    } catch (e) {
      console.error('Error fetching borrowers:', e);
      return {
        data: {
          dynamicBorrowers: [],
          nonBorrowingPartiesList: [],
          borrowersToRender: [],
        },
      };
    }
  }, [loanNumber, token, reset]);

  // Fetch communication data using react-query
  const { data, isFetching, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      return fetchBorrowersInApplicationOrder();
    },
    refetchOnWindowFocus: false,
  });

  const refetchBorrowersInOrder = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey],
  );

  return {
    loadingBorrowersInOrder: isFetching || isLoading,
    dynamicBorrowers: data?.data ? data.data.dynamicBorrowers : [],
    nonBorrowingPartiesList: data?.data ? data.data.nonBorrowingPartiesList : [],
    borrowersToRender: data?.data ? data.data.borrowersToRender : [],
    refetchBorrowersInOrder,
  };
};

export default useBorrowersInOrder;
